const ACCOUNT_ROUTE_GROUPS = Object.freeze({
  "en-US": Object.freeze({
    basePath: "/account/",
    areas: Object.freeze({
      identity: "/account/",
      privacy: "/account/privacy/",
      browser: "/account/browser/",
    }),
    panels: Object.freeze({
      profile: "/account/",
      password: "/account/password/",
      oidc: "/account/connections/",
      passkey: "/account/passkeys/",
      webauthn: "/account/security-keys/",
      totp: "/account/authenticator/",
      lookup_secret: "/account/recovery-codes/",
      sessions: "/account/sessions/",
      "delete-account": "/account/delete/",
    }),
    flows: Object.freeze({
      registration: "/account/register/",
      recovery: "/account/recover/",
    }),
    reauthentication: Object.freeze({
      administrator: Object.freeze({
        path: "/account/confirm-administration/",
        returnPath: "/admin/accounts/",
      }),
    }),
  }),
  "de-DE": Object.freeze({
    basePath: "/de-DE/konto/",
    areas: Object.freeze({
      identity: "/de-DE/konto/",
      privacy: "/de-DE/konto/datenschutz/",
      browser: "/de-DE/konto/browser/",
    }),
    panels: Object.freeze({
      profile: "/de-DE/konto/",
      password: "/de-DE/konto/passwort/",
      oidc: "/de-DE/konto/verknuepfungen/",
      passkey: "/de-DE/konto/passkeys/",
      webauthn: "/de-DE/konto/sicherheitsschluessel/",
      totp: "/de-DE/konto/authenticator/",
      lookup_secret: "/de-DE/konto/wiederherstellungscodes/",
      sessions: "/de-DE/konto/sitzungen/",
      "delete-account": "/de-DE/konto/loeschen/",
    }),
    flows: Object.freeze({
      registration: "/de-DE/konto/registrieren/",
      recovery: "/de-DE/konto/wiederherstellen/",
    }),
    reauthentication: Object.freeze({
      administrator: Object.freeze({
        path: "/de-DE/konto/administration-bestaetigen/",
        returnPath: "/de-DE/admin/konten/",
      }),
    }),
  }),
});

const ROUTES_BY_PATH = new Map();
for (const [locale, group] of Object.entries(ACCOUNT_ROUTE_GROUPS)) {
  for (const [area, path] of Object.entries(group.areas)) {
    ROUTES_BY_PATH.set(path, Object.freeze({
      locale,
      area,
      panel: area === "identity" ? "profile" : "",
      path,
    }));
  }
  for (const [panel, path] of Object.entries(group.panels)) {
    if (panel === "profile") continue;
    ROUTES_BY_PATH.set(path, Object.freeze({
      locale,
      area: "identity",
      panel,
      path,
    }));
  }
  for (const [startFlow, path] of Object.entries(group.flows)) {
    ROUTES_BY_PATH.set(path, Object.freeze({
      locale,
      area: "identity",
      panel: "",
      startFlow,
      path,
    }));
  }
  for (const reauthentication of Object.values(group.reauthentication)) {
    ROUTES_BY_PATH.set(reauthentication.path, Object.freeze({
      locale,
      area: "identity",
      panel: "",
      startFlow: "aal2",
      returnPath: reauthentication.returnPath,
      path: reauthentication.path,
    }));
  }
}

export function accountRouteForPathname(pathname) {
  return ROUTES_BY_PATH.get(String(pathname ?? "")) ?? null;
}

export function accountBasePath(locationLike) {
  return String(locationLike?.pathname ?? "").startsWith("/de-DE/konto/")
    ? ACCOUNT_ROUTE_GROUPS["de-DE"].basePath
    : ACCOUNT_ROUTE_GROUPS["en-US"].basePath;
}

export function currentAccountReturnPath(locationLike) {
  return accountRouteForPathname(locationLike?.pathname)?.path
    ?? accountBasePath(locationLike);
}

export function accountReturnPathFromUrl(value, pageOrigin) {
  let url;
  try {
    url = new URL(value);
  } catch (_error) {
    return "";
  }
  const route = accountRouteForPathname(url.pathname);
  if (
    !route
    || url.origin !== pageOrigin
    || url.username
    || url.password
    || url.hash
    || url.search
  ) {
    return "";
  }
  return route.path;
}

export function accountSettingsPanelUrls(pageOrigin, pathname) {
  const locale = accountRouteForPathname(pathname)?.locale
    ?? (String(pathname ?? "").startsWith("/de-DE/konto/") ? "de-DE" : "en-US");
  return Object.freeze(Object.fromEntries(
    Object.entries(ACCOUNT_ROUTE_GROUPS[locale].panels).map(([panel, path]) => (
      [panel, new URL(path, pageOrigin).toString()]
    )),
  ));
}

export function accountPrimaryAreaUrls(pageOrigin, pathname) {
  const locale = accountRouteForPathname(pathname)?.locale
    ?? (String(pathname ?? "").startsWith("/de-DE/konto/") ? "de-DE" : "en-US");
  return Object.freeze(Object.fromEntries(
    Object.entries(ACCOUNT_ROUTE_GROUPS[locale].areas).map(([area, path]) => (
      [area, new URL(path, pageOrigin).toString()]
    )),
  ));
}

export function accountFlowUrls(pageOrigin, pathname) {
  const locale = accountRouteForPathname(pathname)?.locale
    ?? (String(pathname ?? "").startsWith("/de-DE/konto/") ? "de-DE" : "en-US");
  return Object.freeze(Object.fromEntries(
    Object.entries(ACCOUNT_ROUTE_GROUPS[locale].flows).map(([flow, path]) => (
      [flow, new URL(path, pageOrigin).toString()]
    )),
  ));
}

export const ACCOUNT_ROUTE_PATHS = Object.freeze(
  [...ROUTES_BY_PATH.keys()].sort(),
);
